<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <sidebar-row>
        <v-row class="pb-4">
          <v-col cols="auto">
            <base-button
              class="mb-2 text-body-2"
              icon="mdi-paperclip"
              translation-path="sidebar.addAttachment"
              @click="openAttachmentsDialog"
            ></base-button>
          </v-col>
        </v-row>
      </sidebar-row>

      <v-row no-gutters>
        <v-col v-if="attachmentsForFeature.length > 0">
          <attachment-list
            :attachments="attachmentsForFeature"
            :expanded="expanded"
            :form-schema="formSchema"
            @deleteFile="openDeleteDialog"
          ></attachment-list>
        </v-col>
        <v-col v-else class="no-content">
          {{ $i18n.t('sidebar.noAttachments') }}
        </v-col>
      </v-row>
    </v-col>
    <main-loader v-else></main-loader>
    <attachments-dialog
      v-if="isAttachmentDialogLoaded"
      :is-visible.sync="isAttachmentVisible"
      :features="features"
      @filesAssigned="checkAssignedAttachments"
      :expanded="expanded"
      :formSchema="formSchema"
    ></attachments-dialog>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t('dialog.title.deleteAttachment')"
      :body="$i18n.t('dialog.body.deleteAttachmentConfirmation', { name: fileToDelete ? fileToDelete.name : '' })"
      @agree="handleFileDelete"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
  </v-row>
</template>

<script>
import { get, call } from 'vuex-pathify';
import { intersection } from 'lodash';

import AttachmentList from '@/components/AttachmentList';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    initFunction: {
      type: Function,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    formSchema: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  name: 'Attachments',
  components: {
    SidebarRow,
    MainLoader,
    AttachmentList,
    AttachmentsDialog: () => import('@/components/AttachmentsDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  data: () => ({
    isDialogLoaded: false,
    isDialogVisible: false,
    isConfirmButtonLoading: false,
    isAttachmentVisible: false,
    isAttachmentDialogLoaded: false,
    isFetched: false,
    fileToDelete: undefined,
    fileUploaded: false,
  }),
  computed: {
    attachmentsForFeature: get('relations/attachmentsForFeature'),
    features() {
      return [{ datasource_name: this.dataSourceName, id: this.featureId }];
    },
  },
  watch: {
    featureId() {
      this.init();
    },
  },
  methods: {
    deleteRelation: call('relations/deleteRelation'),
    checkAssignedAttachments(assignedAttachments) {
      const result = intersection(
        this.attachmentsForFeature.map(el => el.id),
        assignedAttachments.map(el => el.id)
      );
      if (result.length > 0) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.assignedAttachmentsDuplicate'),
        });
      }
    },
    async handleFileDelete() {
      this.isConfirmButtonLoading = true;
      try {
        const payload = {
          data_source_name: this.dataSourceName,
          feature_id: this.featureId,
          features: [this.fileToDelete],
        };
        await this.deleteRelation(payload);
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        await this.init();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.attachmentDeleted'),
        });
        this.isConfirmButtonLoading = false;
      } catch {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.deleteAttachmentFailed'),
        });
        this.isConfirmButtonLoading = false;
      }
      this.isDialogLoaded = false;
      this.isDialogVisible = false;
    },
    openDeleteDialog(file) {
      this.fileToDelete = file;
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    openAttachmentsDialog() {
      this.isAttachmentDialogLoaded = true;
      this.isAttachmentVisible = true;
    },
    async init() {
      await this.initFunction({
        data_source_name: this.dataSourceName,
        feature_id: this.featureId,
      });
      this.isFetched = true;
    },
  },
  created() {
    this.$root.$on('fileUploaded', this.init);
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.no-content {
  font-size: 18px;
  line-height: 16px;
  color: #aaaaaa;
  padding: 1.5rem;
}
</style>
